import { alpha } from "@mui/material/styles";
import {
  STUDIO_ACCENT,
  STUDIO_ACCENT_DEEP,
  STUDIO_FONT,
  STUDIO_INK,
  BLOCK_ICON,
  studioHairline,
  studioInk,
  studioMuted,
  studioPaperBg,
  studioSurface,
} from "../Sidebar/sidebarStudioTokens";

/** Acento estudio (mismo que Login / Sidebar). */
const TON = STUDIO_ACCENT;

/**
 * Mi Dashboard · ritmo Login/Sidebar: papel, hairline, pastillas quietas.
 * Sin glass cyan ni degradados chillones.
 */

export const dashPageBgSx = (theme) => {
  const mode = theme.palette.mode;
  return {
    position: "relative",
    minHeight: "100vh",
    fontFamily: STUDIO_FONT,
    background: `
      radial-gradient(920px 520px at 12% -8%, ${alpha(TON, mode === "dark" ? 0.12 : 0.1)} 0%, transparent 55%),
      radial-gradient(640px 400px at 96% 4%, ${alpha(TON, 0.06)} 0%, transparent 48%),
      ${studioPaperBg(mode)}
    `,
  };
};

export const dashHeroSx = (theme) => {
  const mode = theme.palette.mode;
  return {
    position: "sticky",
    top: 0,
    zIndex: 20,
    mb: 1.5,
    borderRadius: 0,
    overflow: "visible",
    p: 0,
    background: "transparent",
    boxShadow: "none",
    border: "none",
    borderBottom: `1px solid ${studioHairline(mode)}`,
    backdropFilter: "blur(10px)",
    backgroundColor:
      mode === "dark" ? "rgba(22,24,26,0.88)" : "rgba(239,237,234,0.88)",
    "& > *": {
      borderRadius: 0,
      backdropFilter: "none",
      background: "transparent",
    },
  };
};

export const dashToolbarBtnSx = (theme, { primary = false } = {}) => {
  const mode = theme.palette.mode;
  const hairline = studioHairline(mode);
  return {
    textTransform: "none",
    fontFamily: STUDIO_FONT,
    fontWeight: 700,
    borderRadius: "10px",
    px: 1.75,
    ...(primary
      ? {
          backgroundColor: STUDIO_ACCENT_DEEP,
          color: "#fff",
          boxShadow: "none",
          border: `1px solid ${STUDIO_ACCENT_DEEP}`,
          "&:hover": {
            backgroundColor: TON,
            boxShadow: "none",
          },
        }
      : {
          borderColor: hairline,
          color: studioInk(mode),
          backgroundColor: studioSurface(mode),
          "&:hover": {
            borderColor: TON,
            backgroundColor: alpha(TON, 0.08),
            color: STUDIO_ACCENT_DEEP,
          },
        }),
  };
};

/** Card papel + hairline (tiles / BLABLATON / páginas hermanas). */
export const dashGlassCardSx = (
  theme,
  { highlighted = false, hoverLift = false } = {},
) => {
  const mode = theme.palette.mode;
  const hairline = studioHairline(mode);
  return {
    width: "100%",
    minWidth: 0,
    p: { xs: 1.5, sm: 2, md: 2.5 },
    borderRadius: "14px",
    position: "relative",
    overflow: "visible",
    fontFamily: STUDIO_FONT,
    border: "1px solid",
    borderColor: highlighted ? alpha(TON, 0.55) : hairline,
    backdropFilter: "none",
    background: studioSurface(mode),
    boxShadow: highlighted
      ? `inset 0 0 0 1px ${alpha(TON, 0.2)}`
      : "inset 0 1px 0 rgba(255,255,255,0.85)",
    transition:
      "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
    ...(hoverLift
      ? {
          "&:hover": {
            transform: "translateY(-1px)",
            borderColor: alpha(TON, 0.45),
          },
        }
      : {}),
  };
};

export const dashTileSx = (theme, { editing = false } = {}) => ({
  ...dashGlassCardSx(theme, { highlighted: editing, hoverLift: !editing }),
  height: "100%",
  p: 0,
  overflow: "hidden",
});

/** Paleta por sección = mismos tonos que el sidebar. */
export const DASH_SECTION_ACCENTS = {
  widgets: TON,
  dashboard: BLOCK_ICON.dashboard.light,
  crm: BLOCK_ICON.crm.light,
  ai: BLOCK_ICON.ai.light,
  development: BLOCK_ICON.development.light,
  resources: BLOCK_ICON.resources.light,
  tracking: BLOCK_ICON.tracking.light,
  manufacturing: BLOCK_ICON.manufacturing.light,
  administration: BLOCK_ICON.admin.light,
  finance: BLOCK_ICON.cash.light,
  conocimiento: BLOCK_ICON.knowledge.light,
  tools: BLOCK_ICON.tools.light,
  user: BLOCK_ICON.users.light,
};

/** Acentos por id: heredan la sección; overrides solo si aportan claridad. */
export const DASH_ITEM_ACCENTS = {
  unibox: BLOCK_ICON.tools.light,
  clients: BLOCK_ICON.crm.light,
  "kanban-clients": BLOCK_ICON.crm.light,
  "kanban-projects": BLOCK_ICON.crm.light,
  tareas: BLOCK_ICON.crm.light,
  calendar: BLOCK_ICON.crm.light,
  general: BLOCK_ICON.dashboard.light,
  "design-dashboard": BLOCK_ICON.dashboard.light,
  "analitica-pyg": BLOCK_ICON.cash.light,
  "analitica-marca": BLOCK_ICON.cash.light,
  pyg: BLOCK_ICON.cash.light,
  cashflow: BLOCK_ICON.cash.light,
  insights: BLOCK_ICON.cash.light,
  "control-horario": BLOCK_ICON.users.light,
  "widget-my-tasks": BLOCK_ICON.crm.light,
  "widget-novedades": BLOCK_ICON.dashboard.light,
  "widget-rfq-quotes": BLOCK_ICON.development.light,
  "widget-critical-path": BLOCK_ICON.manufacturing.light,
  "cp-gantt": BLOCK_ICON.manufacturing.light,
  "cp-global": BLOCK_ICON.manufacturing.light,
  news: BLOCK_ICON.dashboard.light,
  ranking: BLOCK_ICON.dashboard.light,
  "ranking-ventas": BLOCK_ICON.dashboard.light,
  organigrama: BLOCK_ICON.dashboard.light,
  "ai-search": BLOCK_ICON.ai.light,
  "presentaciones-ai": BLOCK_ICON.ai.light,
  designs: BLOCK_ICON.development.light,
  samples: BLOCK_ICON.development.light,
  lookbook: BLOCK_ICON.development.light,
  estimates: BLOCK_ICON.development.light,
  nominas: BLOCK_ICON.cash.light,
  expenses: BLOCK_ICON.cash.light,
  "expenses-user": BLOCK_ICON.cash.light,
};

export function getDashAccent(catalogId, section, mode = "light") {
  let accent = TON;
  if (catalogId && DASH_ITEM_ACCENTS[catalogId]) {
    accent = DASH_ITEM_ACCENTS[catalogId];
  } else if (section && DASH_SECTION_ACCENTS[section]) {
    accent = DASH_SECTION_ACCENTS[section];
  }
  if (mode === "dark") {
    return lightenHex(accent, 0.14);
  }
  return accent;
}

function lightenHex(hex, amount) {
  try {
    const raw = hex.replace("#", "");
    const num = parseInt(
      raw.length === 3 ? raw.replace(/(.)/g, "$1$1") : raw,
      16,
    );
    const r = Math.min(255, ((num >> 16) & 0xff) + Math.round(255 * amount));
    const g = Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * amount));
    const b = Math.min(255, (num & 0xff) + Math.round(255 * amount));
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
  } catch {
    return hex;
  }
}

/** Pozo de icono: anillo acento suave (como login). */
export const dashIconWellSx = (theme, accent = TON) => {
  const isDark = theme.palette.mode === "dark";
  return {
    width: 52,
    height: 52,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: accent,
    bgcolor: alpha(accent, isDark ? 0.14 : 0.1),
    border: "none",
    boxShadow: `0 0 0 1px ${alpha(accent, 0.28)}`,
  };
};

export const dashCountChipSx = (theme, accent) => {
  const color = accent || TON;
  return {
    ml: "auto",
    height: 24,
    minWidth: 28,
    px: 1,
    py: 0,
    borderRadius: "8px",
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: 0.15,
    textAlign: "center",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: STUDIO_FONT,
    color,
    background: alpha(color, theme.palette.mode === "dark" ? 0.16 : 0.1),
    border: `1px solid ${alpha(color, 0.35)}`,
  };
};

export const dashListRowSx = (theme) => ({
  py: 0.7,
  px: 1.1,
  mx: -0.25,
  borderRadius: "10px",
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: alpha(TON, theme.palette.mode === "dark" ? 0.1 : 0.07),
  },
});

export const dashLinkBtnSx = (theme) => ({
  mt: 1.25,
  alignSelf: "flex-start",
  textTransform: "none",
  fontFamily: STUDIO_FONT,
  fontWeight: 700,
  borderRadius: "10px",
  px: 1.5,
  py: 0.4,
  color: STUDIO_ACCENT_DEEP,
  backgroundColor: alpha(TON, theme.palette.mode === "dark" ? 0.14 : 0.1),
  border: `1px solid ${alpha(TON, 0.35)}`,
  "&:hover": {
    backgroundColor: alpha(TON, theme.palette.mode === "dark" ? 0.22 : 0.16),
  },
});

export const dashEmptySx = (theme) => {
  const mode = theme.palette.mode;
  return {
    mb: 2,
    p: 3,
    borderRadius: "14px",
    textAlign: "center",
    fontFamily: STUDIO_FONT,
    border: `1px dashed ${alpha(TON, 0.45)}`,
    backdropFilter: "none",
    background: studioSurface(mode),
    color: studioMuted(mode),
  };
};

export const dashCatalogPaperSx = (theme) => {
  const mode = theme.palette.mode;
  return {
    width: 320,
    top: 0,
    height: "100%",
    borderLeft: `1px solid ${studioHairline(mode)}`,
    backdropFilter: "none",
    background: studioSurface(mode),
    boxShadow: "none",
    fontFamily: STUDIO_FONT,
  };
};

export { STUDIO_ACCENT, STUDIO_ACCENT_DEEP, STUDIO_FONT, STUDIO_INK };
